import React from 'react';
import {
  Package,
  Power,
  Smartphone,
  UserPlus,
  PlusCircle,
  Wifi,
  Activity,
  Mail,
  Globe,
} from 'lucide-react';

interface StepProps {
  number: number;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Step: React.FC<StepProps> = ({ number, title, icon, children }) => (
  <section className="flex flex-col sm:flex-row gap-4 sm:gap-5 mb-6">
    <div className="flex sm:flex-col items-center sm:items-stretch shrink-0">
      <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-emerald-700 text-white text-xl sm:text-2xl font-bold shadow-sm">
        {number}
      </div>
      <div className="hidden sm:block w-px flex-1 bg-emerald-100 mx-auto mt-2" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-emerald-700 shrink-0">{icon}</span>
        <h2 className="text-lg sm:text-xl font-semibold text-emerald-800 leading-snug break-words">
          {title}
        </h2>
      </div>
      <div className="text-gray-700 text-sm sm:text-base leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  </section>
);

const Tip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-md p-3 sm:p-4 my-3 text-sm sm:text-base">
    <p className="text-gray-800">
      <strong className="text-amber-700">Tip:&nbsp;</strong>
      {children}
    </p>
  </div>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-md p-3 sm:p-4 my-3 text-sm sm:text-base">
    <p className="text-gray-800">
      <strong className="text-amber-700">Note:&nbsp;</strong>
      {children}
    </p>
  </div>
);

const Check: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="text-emerald-600 font-bold mt-0.5 shrink-0">✓</span>
    <span>{children}</span>
  </li>
);

const QuickStartGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold tracking-tight">Smart Eco</h1>
              <a
                href="https://smarteco.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-emerald-50 hover:text-white text-sm sm:text-base mt-1 underline-offset-2 hover:underline"
              >
                <Globe className="w-4 h-4" />
                https://smarteco.ai/
              </a>
              <p className="text-emerald-50 text-sm sm:text-base mt-1">
                Indoor Air Quality Monitor
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="italic text-emerald-50 text-sm sm:text-base">Quick-Start Guide</p>
              <p className="text-emerald-100 text-xs sm:text-sm mt-1">
                Unboxing › Setup › First Reading
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-8">
          {/* Step 1 */}
          <Step number={1} title="Open the box & check contents" icon={<Package className="w-5 h-5" />}>
            <p>Confirm all items are present:</p>
            <ol className="list-[lower-alpha] pl-6 space-y-1">
              <li>Smart Eco IAQ device (1×)</li>
              <li>USB-C power adapter &amp; cable (1×)</li>
              <li>Wall-mount adhesive hooks (2×)</li>
              <li>Quick-start guide</li>
            </ol>
          </Step>

          {/* Step 2 */}
          <Step number={2} title="Place & power on the device" icon={<Power className="w-5 h-5" />}>
            <ol className="list-[lower-alpha] pl-6 space-y-1">
              <li>Place it on a flat surface or fix it to the wall mount.</li>
              <li>
                Plug in the USB-C cable, Power LED glows green on IAQ device.
              </li>
            </ol>
            <ul className="space-y-1 pl-2 sm:pl-6">
              <Check>Place at breathing height (1–1.5 m).</Check>
              <Check>Keep away from doors, windows &amp; open areas.</Check>
              <Check>
                Leave at least 20 cm clear space around the device, so sensors receive
                adequate airflow.
              </Check>
            </ul>
            <Tip>Avoid direct sunlight and proximity to air vents or fans.</Tip>
          </Step>

          {/* Step 3 */}
          <Step number={3} title="Download the Smart Eco app" icon={<Smartphone className="w-5 h-5" />}>
            <p>Scan the QR code or search &lsquo;Smart Eco IAQ&rsquo; on Google Play.</p>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mt-2 p-4 rounded-lg border border-gray-200 bg-gray-50">
              <img
                src={`${process.env.PUBLIC_URL}/SmartEcoAppQRCode.png`}
                alt="Scan to download Smart Eco app from Google Play Store"
                className="w-32 h-32 sm:w-36 sm:h-36 rounded-md bg-white p-2 border border-gray-200"
              />
              <div className="text-center sm:text-left">
                <p className="font-semibold text-emerald-700">Scan to download</p>
                <p className="text-sm text-gray-600">
                  Smart Eco app from
                  <br />
                  Google Play Store
                </p>
              </div>
            </div>
          </Step>

          {/* Step 4 */}
          <Step number={4} title="Create an account or log in" icon={<UserPlus className="w-5 h-5" />}>
            <p>
              Tap <strong>&ldquo;Get Started&rdquo;</strong>, enter your details &amp; verify your email.
            </p>
            <p>
              Existing users: tap <strong>&ldquo;Sign In&rdquo;</strong> and enter your credentials.
            </p>
          </Step>

          {/* Step 5 */}
          <Step number={5} title="Add your Smart Eco device" icon={<PlusCircle className="w-5 h-5" />}>
            <ol className="list-[lower-alpha] pl-6 space-y-1">
              <li>
                In the app tap <strong>&ldquo;+&rdquo;</strong> &rsaquo; <strong>Add Device</strong>.
              </li>
              <li>
                Scan the QR code on the device base OR enter the Serial Number printed on the
                device label.
              </li>
            </ol>
          </Step>

          {/* Step 6 */}
          <Step number={6} title="Connect to Wi-Fi" icon={<Wifi className="w-5 h-5" />}>
            <ol className="list-[lower-alpha] pl-6 space-y-1">
              <li>Follow the steps shown in the app.</li>
              <li>
                Select your <strong>2.4 GHz</strong> network, enter password, tap{' '}
                <strong>&ldquo;Connect&rdquo;</strong>.
              </li>
              <li>Wi-Fi LED ring turns green when connected.</li>
            </ol>
            <Note>2.4 GHz networks only, 5 GHz networks will not connect.</Note>
          </Step>

          {/* Step 7 */}
          <Step number={7} title="View your first air quality reading" icon={<Activity className="w-5 h-5" />}>
            <p>Live readings of all parameters will appear within 5 minutes.</p>
            <Note>Allow 10–15 min warm-up for sensors to stabilize on first use.</Note>
          </Step>

          {/* Help footer */}
          <section className="mt-8 rounded-lg bg-emerald-700 text-white p-5 sm:p-6">
            <h3 className="text-lg sm:text-xl font-semibold mb-3">Still Need help?</h3>
            <div className="space-y-2 text-sm sm:text-base">
              <p className="flex items-start gap-2 break-words">
                <Mail className="w-5 h-5 mt-0.5 shrink-0" />
                <span>
                  <strong>Email:&nbsp;</strong>
                  <a
                    href="mailto:supportsmarteco@ispatialtec.com"
                    className="underline-offset-2 hover:underline break-all"
                  >
                    supportsmarteco@ispatialtec.com
                  </a>
                </span>
              </p>
            </div>
            <p className="italic text-emerald-50 text-xs sm:text-sm mt-4">
              Powered by iSpatial Techno Solutions Inc.
              <br />
              <a
                href="https://ispatialtec.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-2 hover:underline"
              >
                https://ispatialtec.com/
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default QuickStartGuidePage;
